import { BoutonCopier, Carte, Encart, Entete } from '../composants'

/**
 * Guide de configuration des automatisations Raccourcis.
 *
 * Page a part entiere et non note de bas de page : sans ces automatisations,
 * l'application ne capte rien du tout. C'est la seule partie du produit qui se
 * monte a la main, et la seule qu'on ne peut pas corriger a distance.
 */
export function PageInstallation() {
  // Construite depuis l'origine courante : la meme page sert en developpement
  // sur l'IP du reseau local et en production, sans adresse codee en dur qui
  // finirait par diverger.
  const base = `${window.location.origin}/#/sync/`

  return (
    <>
      <Entete titre="Configurer les raccourcis" retour />
      <main className="contenu">
        <Encart ton="info">
          iOS n’autorise aucune application web à s’exécuter en arrière-plan : dès que
          l’écran s’éteint, le code de cette page est gelé. Elle ne peut donc rien observer
          d’elle-même. Ce sont les automatisations ci-dessous qui relèvent les heures — et
          un raccourci ne pouvant pas écrire dans le stockage d’un site, elles déposent les
          relevés dans un fichier, que celle du matin transmet en ouvrant une adresse.
        </Encart>

        <Carte titre="L’adresse de synchronisation">
          <p className="doux">
            À recopier dans l’automatisation nº 5. Tout ce qui suit le <code>#</code> reste
            dans le téléphone : un fragment d’URL n’est jamais transmis au serveur, et
            n’apparaît donc dans aucun journal.
          </p>
          <p style={{ marginTop: 10 }}>
            <code className="code code--bloc">{base}</code>
          </p>
          <div style={{ marginTop: 10 }}>
            <BoutonCopier texte={base} libelle="Copier l’adresse" />
          </div>
        </Carte>

        <Carte titre="Avant de commencer">
          <ol className="etapes">
            <li className="etape">
              <div>
                <strong>Installer l’app sur l’écran d’accueil</strong>
                <p className="doux">
                  Dans Safari : Partager, puis « Sur l’écran d’accueil ». Installée, l’app
                  garde ses données durablement ; laissée dans un onglet Safari, elles
                  peuvent être purgées après quelques jours d’inactivité.
                </p>
              </div>
            </li>
            <li className="etape">
              <div>
                <strong>Ouvrir Raccourcis → onglet Automatisation</strong>
                <p className="doux">
                  Chaque automatisation ci-dessous se crée avec « Nouvelle automatisation ».
                  Pour toutes : choisir <strong>« Exécuter immédiatement »</strong> et
                  <strong> désactiver « Avertir à l’exécution »</strong>. Sans cela, chaque
                  relevé demanderait une confirmation, y compris en pleine nuit.
                </p>
              </div>
            </li>
          </ol>
        </Carte>

        <h2>Les trois indispensables</h2>
        <p className="doux">
          Elles suffisent à obtenir des nuits. Le début vient du chargeur, la fin de
          l’alarme.
        </p>

        <Automatisation
          numero={1}
          declencheur="Chargeur → Est connecté"
          lettre="P"
          fichier="nuit.txt"
        />
        <Automatisation
          numero={2}
          declencheur="Chargeur → Est déconnecté"
          lettre="U"
          fichier="nuit.txt"
        />

        <Carte titre="3. Alarme → Est arrêtée">
          <p className="doux">
            La plus longue : elle relève l’heure du lever, transmet la nuit à l’app, en
            garde une copie, puis vide le fichier de travail.
          </p>
          <ol className="recette" style={{ marginTop: 10 }}>
            <li>
              <strong>Date</strong> — donne l’instant courant
            </li>
            <li>
              <strong>Formater la date</strong> — format personnalisé{' '}
              <code>yyyyMMddHHmm</code>
            </li>
            <li>
              <strong>Texte</strong> — <code>A</code> puis la <em>Date formatée</em> puis{' '}
              <code>-</code>
            </li>
            <li>
              <strong>Ajouter au fichier texte</strong> — fichier <code>nuit.txt</code>
            </li>
            <li>
              <strong>Obtenir le contenu du fichier</strong> — <code>nuit.txt</code>
            </li>
            <li>
              <strong>Ajouter au fichier texte</strong> — fichier{' '}
              <code>historique.txt</code>, entrée : le <em>Contenu du fichier</em>
            </li>
            <li>
              <strong>Texte</strong> — laisser <em>vide</em>
            </li>
            <li>
              <strong>Enregistrer le fichier</strong> — <code>nuit.txt</code>, avec{' '}
              <em>Écraser si le fichier existe</em> activé
            </li>
            <li>
              <strong>Texte</strong> — l’adresse copiée plus haut, suivie du{' '}
              <em>Contenu du fichier</em> de l’étape 5
            </li>
            <li>
              <strong>Ouvrir les URL</strong>
            </li>
          </ol>
          <Encart ton="alerte">
            <strong>L’ordre compte.</strong> « Ouvrir les URL » bascule vers l’application
            et peut interrompre le raccourci sur-le-champ : tout ce qui doit être fait —
            archiver, vider — doit donc le précéder. C’est aussi pourquoi une copie part
            dans <code>historique.txt</code> : si l’ouverture échoue, les relevés sont
            récupérables depuis Réglages.
          </Encart>
        </Carte>

        <h2>Les deux qui détectent les réveils</h2>
        <p className="doux">
          Facultatives, mais ce sont elles qui permettent de savoir que le téléphone a servi
          à 3 h — et donc d’arbitrer si la nuit doit en tenir compte. Sans elles, une nuit
          reste un bloc.
        </p>

        <Automatisation
          numero={4}
          declencheur="App → Est ouverte"
          lettre="O"
          fichier="nuit.txt"
          note="Choisir les apps réellement consultées au lit — messagerie, navigateur, réseaux. Trois à six suffisent : c'est le meilleur moyen de garder le fichier court tout en captant ce qui compte."
        />
        <Automatisation
          numero={5}
          declencheur="App → Est fermée"
          lettre="V"
          fichier="nuit.txt"
          note="Exactement les mêmes apps que la précédente. L'ouverture dit depuis quand le téléphone a servi, la fermeture jusqu'à quand : il faut les deux pour connaître la durée d'un réveil."
        />

        <Carte titre="Vérifier que ça marche">
          <p className="doux">
            Brancher puis débrancher le chargeur : deux relevés doivent apparaître dans le
            compteur de la page Réglages. La première nuit complète remontera d’elle-même
            au réveil suivant, à l’arrêt de l’alarme.
          </p>
          <p className="doux" style={{ marginTop: 10 }}>
            Les automatisations personnelles d’iOS ratent parfois un déclenchement, sans
            alerte. C’est pourquoi deux familles de signaux sont conservées, pourquoi les
            nuits impossibles à former sont signalées, et pourquoi toute nuit reste
            corrigeable à la main.
          </p>
        </Carte>
      </main>
    </>
  )
}

function Automatisation({
  numero,
  declencheur,
  lettre,
  fichier,
  note,
}: {
  numero: number
  declencheur: string
  lettre: string
  fichier: string
  note?: string
}) {
  return (
    <Carte titre={`${numero}. ${declencheur}`}>
      {note && <p className="doux">{note}</p>}
      <ol className="recette" style={{ marginTop: note ? 10 : 0 }}>
        <li>
          <strong>Date</strong> — donne l’instant courant
        </li>
        <li>
          <strong>Formater la date</strong> — format personnalisé <code>yyyyMMddHHmm</code>
        </li>
        <li>
          <strong>Texte</strong> — <code>{lettre}</code> puis la <em>Date formatée</em> puis{' '}
          <code>-</code>
        </li>
        <li>
          <strong>Ajouter au fichier texte</strong> — fichier <code>{fichier}</code>
        </li>
      </ol>
    </Carte>
  )
}
